/**
 * Small, pure formatting helpers.
 *
 * Everything here is written for a beginner reading a phone screen: short
 * strings, no jargon, no ambiguity about which day "Sunday" means.
 */

const DAY = 24 * 60 * 60 * 1000;

export function formatKickoffTime(timestamp) {
  if (!timestamp) return "";
  return new Date(timestamp).toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

export function formatKickoffDate(timestamp) {
  if (!timestamp) return "";
  return new Date(timestamp).toLocaleDateString(undefined, {
    weekday: "long",
    month: "short",
    day: "numeric",
  });
}

/** "Today", "Tomorrow", or the weekday, whichever a person would say. */
export function formatRelativeDay(timestamp, now = Date.now()) {
  if (!timestamp) return "";

  const startOfDay = (ms) => {
    const date = new Date(ms);
    date.setHours(0, 0, 0, 0);
    return date.getTime();
  };

  const days = Math.round((startOfDay(timestamp) - startOfDay(now)) / DAY);

  if (days === 0) return "Today";
  if (days === 1) return "Tomorrow";
  if (days === -1) return "Yesterday";
  if (days > 1 && days < 7) {
    return new Date(timestamp).toLocaleDateString(undefined, { weekday: "long" });
  }
  return formatKickoffDate(timestamp);
}

/** "Today · 7:00 PM" */
export function formatKickoffLabel(timestamp, now = Date.now()) {
  if (!timestamp) return "Time to be confirmed";
  return `${formatRelativeDay(timestamp, now)} · ${formatKickoffTime(timestamp)}`;
}

/**
 * "just now", "18 seconds ago", "3 minutes ago", for staleness copy.
 *
 * Days matter as much as seconds do here. Live game data is never more
 * than minutes old before we say something is wrong, but a saved player
 * profile can legitimately be a week old, and "173 hours ago" is a number
 * nobody can read at a glance.
 */
export function formatTimeAgo(timestamp, now = Date.now()) {
  if (!timestamp) return "never";

  const seconds = Math.max(0, Math.round((now - timestamp) / 1000));
  if (seconds < 5) return "just now";
  if (seconds < 60) return `${seconds} seconds ago`;

  const minutes = Math.round(seconds / 60);
  if (minutes === 1) return "1 minute ago";
  if (minutes < 60) return `${minutes} minutes ago`;

  const hours = Math.round(minutes / 60);
  if (hours === 1) return "1 hour ago";
  if (hours < 24) return `${hours} hours ago`;

  const days = Math.round(hours / 24);
  return days === 1 ? "1 day ago" : `${days} days ago`;
}

/** "+8 yards", "−6 yards", "no gain" */
export function formatYards(yards) {
  const value = Number(yards);
  if (!Number.isFinite(value) || value === 0) return "no gain";
  return value > 0 ? `+${value} yards` : `${value} yards`;
}

export function pluralize(count, singular, plural) {
  return Number(count) === 1 ? singular : plural ?? `${singular}s`;
}
